import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { attachOfferPools, getOfferPoolCoverage, type OfferPoolAttachReport } from '@/api/tracker-offer-pools';
import type { Offer } from '@/api/types';
import type { TrackerCampaign } from '@/api/tracker-types';

interface Gap {
  offer: Offer;
  missing: TrackerCampaign[];
}

/** One key per offer/campaign pair the user may tick. */
const pairKey = (offerId: string, campaignId: string) => `${offerId}|${campaignId}`;

function offerLabel(o: Offer): string {
  return [o.partner, o.brand && o.brand !== o.partner ? o.brand : null].filter(Boolean).join(' · ');
}

/**
 * Shows every active offer that is missing from at least one pool campaign,
 * lets the user tick which pairs to fill, and attaches them in one request.
 * `campaigns` is the page's own notion of "current" pool campaigns (offer_pool,
 * not backed by a banned app), so the gaps match the per-offer picker.
 */
export function PoolCoverageModal({
  isOpen, onClose, offers, campaigns,
}: {
  isOpen: boolean;
  onClose: () => void;
  offers: Offer[];
  campaigns: TrackerCampaign[];
}) {
  const queryClient = useQueryClient();
  const { data: coverage = [], isLoading, error, refetch } = useQuery({
    queryKey: ['offer-pool-coverage'],
    queryFn: getOfferPoolCoverage,
    enabled: isOpen,
    staleTime: 0,
  });

  const gaps = useMemo<Gap[]>(() => {
    if (campaigns.length === 0) return [];
    const byOffer = new Map(coverage.map((row) => [row.offer_id, new Set(row.campaign_ids)]));
    const offerById = new Map(offers.map((o) => [o.id, o]));
    const out: Gap[] = [];
    for (const row of coverage) {
      const offer = offerById.get(row.offer_id);
      if (!offer || offer.status !== 'active' || row.geo_count === 0) continue;
      const have = byOffer.get(row.offer_id) ?? new Set<string>();
      const missing = campaigns.filter((c) => !have.has(c.id));
      if (missing.length) out.push({ offer, missing });
    }
    return out.sort((a, b) => b.missing.length - a.missing.length || offerLabel(a.offer).localeCompare(offerLabel(b.offer)));
  }, [coverage, offers, campaigns]);

  // Everything is ticked by default: the common case is "fill all gaps".
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<OfferPoolAttachReport | null>(null);
  const [submitError, setSubmitError] = useState('');
  useEffect(() => {
    if (!isOpen) { setReport(null); setSubmitError(''); return; }
    setSelected(new Set(gaps.flatMap((g) => g.missing.map((c) => pairKey(g.offer.id, c.id)))));
  }, [isOpen, gaps]);

  const totalPairs = gaps.reduce((n, g) => n + g.missing.length, 0);
  const toggle = (keys: string[], on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    return next;
  });
  const campaignKeys = (campaignId: string) =>
    gaps.filter((g) => g.missing.some((c) => c.id === campaignId)).map((g) => pairKey(g.offer.id, campaignId));

  const submit = async () => {
    const items = gaps
      .map((g) => ({ offer_id: g.offer.id, campaign_ids: g.missing.filter((c) => selected.has(pairKey(g.offer.id, c.id))).map((c) => c.id) }))
      .filter((i) => i.campaign_ids.length > 0);
    if (items.length === 0) return;
    setBusy(true);
    setSubmitError('');
    try {
      setReport(await attachOfferPools(items));
      queryClient.invalidateQueries({ queryKey: ['tracker-flows'] });
      queryClient.invalidateQueries({ queryKey: ['flow-offers'] });
      queryClient.invalidateQueries({ queryKey: ['offer-pool-campaigns'] });
      await refetch();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Campaigns that at least one listed offer is missing, for the column toggles.
  const missingCampaigns = useMemo(() => {
    const ids = new Set(gaps.flatMap((g) => g.missing.map((c) => c.id)));
    return campaigns.filter((c) => ids.has(c.id));
  }, [gaps, campaigns]);

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!busy) onClose(); }} title="Offers missing from campaigns" size="3xl">
      <div className="space-y-4">
        <p className="text-sm text-content-muted">
          Active offers that are not attached to every current pool campaign. Tick the pairs to connect and attach them
          in one go. Nothing is ever detached here; each offer lands on the <code className="px-1 bg-surface-muted rounded">AUTO &lt;GEO&gt;</code> flow
          for every GEO it serves.
        </p>

        {isLoading && <div className="py-8 text-center text-sm text-content-subtle">Loading coverage…</div>}
        {error && <div className="text-sm text-danger">Could not load coverage: {error instanceof Error ? error.message : 'Unknown error'}</div>}

        {!isLoading && !error && gaps.length === 0 && (
          <div className="py-8 text-center text-sm text-content-muted">
            {campaigns.length === 0 ? 'No pool campaigns yet.' : 'Every active offer is already in every pool campaign ✅'}
          </div>
        )}

        {gaps.length > 0 && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="text-content-muted">
                <span className="font-medium text-content">{gaps.length}</span> offers · <span className="font-medium text-content">{totalPairs}</span> missing links · selected <span className="font-medium text-content">{selected.size}</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => toggle(gaps.flatMap((g) => g.missing.map((c) => pairKey(g.offer.id, c.id))), true)} disabled={busy}>Select all</Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => setSelected(new Set())} disabled={busy}>Clear</Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 text-xs">
              <span className="self-center text-content-subtle">By campaign:</span>
              {missingCampaigns.map((c) => {
                const keys = campaignKeys(c.id);
                const on = keys.every((k) => selected.has(k));
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={busy}
                    onClick={() => toggle(keys, !on)}
                    className={`rounded border px-2 py-0.5 ${on ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border text-content-muted hover:bg-surface-muted'}`}
                    title={on ? 'Unselect this campaign for all offers' : 'Select this campaign for all offers'}
                  >{c.name} · {keys.length}</button>
                );
              })}
            </div>

            <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border divide-y divide-border">
              {gaps.map((g) => {
                const keys = g.missing.map((c) => pairKey(g.offer.id, c.id));
                const picked = keys.filter((k) => selected.has(k)).length;
                return (
                  <div key={g.offer.id} className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="rounded border-border-strong"
                        checked={picked === keys.length}
                        ref={(el) => { if (el) el.indeterminate = picked > 0 && picked < keys.length; }}
                        onChange={(e) => toggle(keys, e.target.checked)}
                        disabled={busy}
                      />
                      <span className="font-medium text-content">{offerLabel(g.offer)}</span>
                      <span className="text-xs text-content-subtle">{g.offer.geo}{g.offer.lang ? ` · ${g.offer.lang}` : ''}</span>
                      <span className="ml-auto text-xs text-content-subtle">missing {g.missing.length}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5 pl-6">
                      {g.missing.map((c) => {
                        const k = pairKey(g.offer.id, c.id);
                        const on = selected.has(k);
                        return (
                          <label key={c.id} className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-0.5 text-xs ${on ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border text-content-muted'}`}>
                            <input type="checkbox" className="h-3 w-3" checked={on} onChange={(e) => toggle([k], e.target.checked)} disabled={busy} />
                            {c.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {report && (
          <div className="rounded-md border border-success/30 bg-success-soft px-3 py-2 text-sm text-content">
            Attached <span className="font-medium">{report.offers_attached}</span> offer→GEO links across <span className="font-medium">{report.offers_processed}</span> offers, created <span className="font-medium">{report.flows_created}</span> AUTO flows.
            {report.errors.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-danger">{report.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            )}
          </div>
        )}
        {submitError && <div className="text-sm text-danger">{submitError}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
          {gaps.length > 0 && (
            <Button type="button" onClick={submit} disabled={busy || selected.size === 0}>
              {busy ? 'Attaching…' : `Attach selected (${selected.size})`}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
